"use client"

import { useSyncExternalStore } from "react"
import { useSession } from "next-auth/react"

import {
  AUTH_TEST_MODE,
  TEST_USER,
  isTestSignedIn,
  subscribeTestAuth,
} from "@/lib/test-auth"

export interface AppUser {
  /** Stable Entra object id (oid) from the server session. */
  userId: string
  name: string
  email: string
}

const serverSnapshot = () => false

/** Returns the signed-in user from the BFF session, or null if signed out. */
export function useAppUser(): { user: AppUser | null; loading: boolean } {
  const { data: session, status } = useSession()
  // E2E auth seam (test-auth.ts): subscribed unconditionally (hooks rule), but
  // outside test mode the snapshot is constant `false` and never emits.
  const testSignedIn = useSyncExternalStore(
    subscribeTestAuth,
    isTestSignedIn,
    serverSnapshot
  )

  if (AUTH_TEST_MODE) {
    return testSignedIn
      ? { user: { ...TEST_USER }, loading: false }
      : { user: null, loading: false }
  }

  // The session cookie is still being resolved via GET /auth/session.
  if (status === "loading") return { user: null, loading: true }

  // `ReauthRequired` means the server-held refresh token is permanently dead —
  // treat as signed out so the AuthGate routes the user back to sign-in.
  if (
    status === "authenticated" &&
    session?.userId &&
    session.error !== "ReauthRequired"
  ) {
    const email = session.user?.email ?? ""
    return {
      user: {
        userId: session.userId,
        name: session.user?.name ?? email,
        email,
      },
      loading: false,
    }
  }

  return { user: null, loading: false }
}
